import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getBrowser, getOS, getDeviceType } from '@/lib/botDetection';
import { isProductionDomain } from '@/lib/productionDomainCheck';

function getVisitorId(): string {
  if (typeof window === 'undefined') return 'server';
  let id = localStorage.getItem('xpand_visitor_id');
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('xpand_visitor_id', id);
  }
  return id;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  let id = sessionStorage.getItem('xpand_session_id');
  if (!id) {
    id = `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('xpand_session_id', id);
  }
  return id;
}

interface ErrorLog {
  type: 'runtime_error' | 'promise_rejection' | 'react_error' | 'api_error' | 'network_error';
  message: string;
  stack?: string;
}

export function useErrorTracking() {
  const isProduction = useRef<boolean>(isProductionDomain());

  const logError = useCallback(async (error: ErrorLog) => {
    // Skip tracking for non-production environments
    if (!isProduction.current) return;
    try {
      const visitorId = getVisitorId();
      const sessionId = getSessionId();

      await supabase.from('error_logs').insert([{
        visitor_id: visitorId,
        session_id: sessionId,
        page_path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        error_type: error.type,
        error_message: error.message,
        stack_trace: error.stack || null,
        browser: getBrowser(),
        os: getOS(),
        device_type: getDeviceType(),
      }]);

      console.log(`[Error Tracking] Logged: ${error.type}`, error.message);
    } catch (err) {
      // Silently fail - don't create infinite error loop
      console.error('[Error Tracking] Failed to log error:', err);
    }
  }, []);

  const trackApiError = useCallback(async (
    endpoint: string,
    status: number,
    message: string
  ) => {
    await logError({
      type: 'api_error',
      message: `API Error: ${endpoint} - ${status} - ${message}`,
    });
  }, [logError]);

  const trackNetworkError = useCallback(async (
    url: string,
    message: string
  ) => {
    await logError({
      type: 'network_error',
      message: `Network Error: ${url} - ${message}`,
    });
  }, [logError]);

  useEffect(() => {
    // Global error handler
    const handleGlobalError = (event: ErrorEvent) => {
      logError({
        type: 'runtime_error',
        message: event.message,
        stack: event.error?.stack,
      });
    };

    // Unhandled promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason);
      logError({
        type: 'promise_rejection',
        message,
        stack: event.reason?.stack,
      });
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [logError]);

  return {
    logError,
    trackApiError,
    trackNetworkError,
  };
}

// React Error Boundary wrapper for tracking React errors
export async function trackReactError(error: Error, errorInfo: React.ErrorInfo) {
  // Skip tracking for non-production environments
  if (!isProductionDomain()) return;

  const visitorId = typeof window !== 'undefined' 
    ? localStorage.getItem('xpand_visitor_id') || 'unknown' 
    : 'server';
  const sessionId = typeof window !== 'undefined' 
    ? sessionStorage.getItem('xpand_session_id') || 'unknown' 
    : 'server';

  try {
    await supabase.from('error_logs').insert([{
      visitor_id: visitorId,
      session_id: sessionId,
      page_path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
      error_type: 'react_error',
      error_message: error.message,
      stack_trace: `${error.stack}\n\nComponent Stack:${errorInfo.componentStack}`,
      browser: getBrowser(),
      os: getOS(),
      device_type: getDeviceType(),
    }]);
    console.log('[Error Tracking] React error logged');
  } catch (err) {
    console.error('[Error Tracking] Failed to log React error:', err);
  }
}
